import * as React from "react";
import { sendGetRequest, useGetRequest, sendPostRequest } from "../../Client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Button, Table, Modal } from "flowbite-react";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Notyf } from "notyf";
import "notyf/notyf.min.css"; // for React, Vue and Svelte
import Header from "../../Components/Header";
import CsvFileInput from "../../Components/CsvFileImport";
import ReactLoading from "react-loading";
import dateFormat, { masks } from "dateformat";

export default function AdvisorStatements() {
  const [loading, setLoading] = useState(false);

  const queryClient = useQueryClient();

  const [openModal, setOpenModal] = useState(false);
  const [inputs, setInputs] = useState({});

  const [searchFromDate, setSearchFromDate] = useState("");
  const [searchToDate, setSearchToDate] = useState("");
  const [searchAdvisor, setSearchAdvisor] = useState("");

  const [fetchedData, setFetchedData] = useState([]);
  const [searched, setSearched] = useState(false);

  const [selectedIds, setSelectedIds] = useState(new Set());
  const headerCheckboxRef = React.useRef(null);

  const statements = fetchedData?.statements ?? [];

  function onCloseModal() {
    setOpenModal(false);
  }

  const normId = (id) => String(id);

  const allIds = React.useMemo(
    () => statements.map((s) => normId(s.submittedID)),
    [statements]
  );

  const isAllChecked =
    selectedIds.size > 0 && selectedIds.size === allIds.length;
  const isIndeterminate =
    selectedIds.size > 0 && selectedIds.size < allIds.length;

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = isIndeterminate;
    }
  }, [isIndeterminate]);

  const toggleOne = (rawId) => {
    const id = normId(rawId);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setSelectedIds((prev) => {
      if (prev.size === allIds.length) return new Set(); // uncheck all
      return new Set(allIds); // check all
    });
  };

  const selectedTotal = React.useMemo(() => {
    if (!statements.length || selectedIds.size === 0) return 0;
    return statements.reduce((sum, s) => {
      if (selectedIds.has(normId(s.submittedID))) {
        return sum + (parseFloat(s.advisorNetFeePayableAmount) || 0);
      }
      return sum;
    }, 0);
  }, [statements, selectedIds]);

  const navigate = useNavigate();

  const notyf = new Notyf({
    position: {
      x: "right",
      y: "top",
    },
  });

  function getCurrencySymbol(currency) {
    if (currency == 1) {
      return "£";
    } else if (currency == 2) {
      return "$";
    } else if (currency == 3) {
      return "€";
    } else if (currency == 4) {
      return "CZK";
    } else {
      return "£";
    }
  }

  const { status: statusAdvisors, data: dataAdvisors } = useGetRequest(
    "people/dropdown",
    {}
  );

  const fetchPosts = async () => {
    setLoading(true);

    if (!searchAdvisor) {
      notyf.error("Please select an advisor.");
      setLoading(false);
      return;
    }

    const requestBody = {
      fromDate: searchFromDate,
      toDate: searchToDate,
      advisor: searchAdvisor,
    };

    const res = await sendPostRequest("statements/advisor/search", requestBody);

    setFetchedData(res);

    setLoading(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSearched(true);
    fetchPosts();
  };

  const totalAdvisorFee = fetchedData?.statements?.reduce((sum, statement) => {
    return sum + (parseFloat(statement.advisorNetFeePayableAmount) || 0);
  }, 0);

  const addedNotyf = () => {
    notyf.success("Statement added");
  };

  const statementAdd = async (path, body) => {
    const res = await sendPostRequest(path, body);
    fetchPosts();
    addedNotyf();
  };

  const { mutateAsync: doAddMutation } = useMutation({
    mutationFn: (data) => {
      return statementAdd(data.path, { ...data.body });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["statements"] });
    },
  });

  if (statusAdvisors === "fetched") {
    return (
      <>
        <Header />

        <section className="bg-gray-100 dark:bg-gray-900 py-3 sm:py-5">
          <div className="px-4 mx-auto max-w-screen-2xl lg:px-12 mb-80">
            <div className="relative overflow-hidden bg-white shadow-md dark:bg-gray-800 sm:rounded-lg">
              <div className="border-b border-gray-200 pb-5 sm:flex sm:items-center sm:justify-between w-[94%] ml-[3%] mt-[3%] pb-6">
                <h3 className="text-base font-semibold text-gray-900">
                  Advisor Statements
                </h3>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="px-2 py-2 mt-5">
                  <div className="w-full flex flex-row mt-4 justify-center align-end">
                    <div className="w-1/6">
                      <div className="mb-5 mx-2">
                        <label
                          htmlFor="advisor"
                          className="block text-sm font-medium leading-6 text-gray-900"
                        >
                          Advisor
                        </label>
                        <select
                          id="advisor"
                          name="advisor"
                          onChange={(e) => setSearchAdvisor(e.target.value)}
                          className="mt-2 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:max-w-xs sm:text-sm sm:leading-6"
                        >
                          <option value="">Select an advisor</option>
                          {dataAdvisors?.people.map((person) => (
                            <option
                              key={person.personID}
                              value={person.personID}
                            >
                              {person.firstName} {person.surname}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="w-1/6">
                      <div className="mb-5 mx-2">
                        <div className="sm:col-span-2">
                          <label
                            htmlFor="fromDate"
                            className="block text-sm font-medium leading-6 text-gray-900"
                          >
                            From Date
                          </label>
                          <div className="mt-2">
                            <input
                              type="date"
                              name="fromDate"
                              id="fromDate"
                              onChange={(e) =>
                                setSearchFromDate(e.target.value)
                              }
                              className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="w-1/6">
                      <div className="sm:col-span-2">
                        <label
                          htmlFor="toDate"
                          className="block text-sm font-medium leading-6 text-gray-900"
                        >
                          To Date
                        </label>
                        <div className="mt-2">
                          <input
                            type="date"
                            name="toDate"
                            id="toDate"
                            onChange={(e) => setSearchToDate(e.target.value)}
                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                          />
                        </div>
                      </div>
                    </div>

                    <button
                      type="submit"
                      className="w-20 ml-8 mt-8 h-10 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                    >
                      Search
                    </button>

 
                  </div>
                </div>
              </form>

              {searched && statements.length > 0 && (
                <div className="flex items-center justify-between px-6 pb-2">
                  <div className="text-sm text-gray-600">
                    Selected:{" "}
                    <span className="font-semibold">{selectedIds.size}</span>
                    {selectedIds.size > 0 && (
                      <>
                        {" · "}Selected Total:{" "}
                        <span className="font-semibold">
                          {getCurrencySymbol(statements[0]?.currencyID)}
                          {selectedTotal.toFixed(2)}
                        </span>
                      </>
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={() => setOpenModal(true)}
                    disabled={selectedIds.size === 0 || loading}
                    className={`rounded-md px-4 py-2 text-sm font-semibold text-white shadow-sm
        ${
          selectedIds.size === 0 || loading
            ? "bg-gray-300 cursor-not-allowed"
            : "bg-green-600 hover:bg-green-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600"
        }`}
                  >
                    Create Statement
                  </button>
                </div>
              )}

              {searched && (
                <div className="overflow-x-auto p-6">
                  {loading ? (
                    <div className="inline-block min-w-full py-2 flex justify-center sm:px-6 lg:px-8">
                      <ReactLoading type={"spin"} color="#5046e5" />
                    </div>
                  ) : fetchedData?.statements?.length > 0 ? (
                    <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
                      <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
                        <tr>
                          <th scope="col" className="px-4 py-3 w-10">
                            <input
                              ref={headerCheckboxRef}
                              type="checkbox"
                              checked={isAllChecked}
                              onChange={toggleAll}
                              className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                              aria-label="Select all rows"
                            />
                          </th>
                          <th className="px-4 py-3">Date</th>
                          <th className="px-4 py-3">Advisor</th>
                          <th className="px-4 py-3">Client</th>
                          <th className="px-4 py-3">Advisor Fee</th>
                        </tr>
                      </thead>
                      <tbody>
                        {statements.map((statement) => {
                          const id = normId(statement.submittedID);
                          const checked = selectedIds.has(id);

                          return (
                            <tr
                              key={id}
                              className="border-b dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700"
                            >
                              <td className="px-4 py-4 w-10">
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => toggleOne(id)}
                                  className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                  aria-label={`Select statement ${id}`}
                                />
                              </td>

                              <td className="px-4 py-4 text-sm font-medium text-gray-500 whitespace-nowrap dark:text-white">
                                {dateFormat(
                                  statement.submittedStartDate,
                                  "mmmm dS, yyyy"
                                )}
                              </td>
                              <td className="px-4 py-2">
                                {statement.advisorSurname},{" "}
                                {statement.advisorFirstName}
                              </td>
                              <td className="px-4 py-2">
                                {statement.clientSurname},{" "}
                                {statement.clientFirstName}
                              </td>
                              <td className="px-4 py-2">
                                {getCurrencySymbol(statement.currencyID)}
                                {statement.advisorNetFeePayableAmount}
                              </td>
                            </tr>
                          );
                        })}

                        <tr className="bg-gray-100 font-semibold">
                          <td className="px-4 py-3" colSpan={4}>
                            Total Advisor Fee
                          </td>
                          <td className="px-4 py-3">
                            {getCurrencySymbol(statements[0]?.currencyID)}
                            {totalAdvisorFee?.toFixed(2)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  ) : (
                    <div className="flex flex-col items-center justify-center w-full py-12">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-12 w-12 text-gray-400 mb-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth="1.5"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M21 21l-4.35-4.35M10.5 18a7.5 7.5 0 100-15 7.5 7.5 0 000 15z"
                        />
                      </svg>
                      <p className="text-lg font-semibold text-gray-700 mb-1">
                        No Results Found
                      </p>
                      <p className="text-sm text-gray-500 text-center max-w-md">
                        We couldn't find any statements for your selected
                        advisor and date range. Try adjusting your search
                        filters.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </section>

        <Modal
          show={openModal}
          size="xl"
          onClose={onCloseModal}
          popup
          className="p-8 md:p-12"
        >
          <Modal.Header>
            <span className="text-xl font-bold p-6 pt-20">
              Confirm statement details
            </span>
          </Modal.Header>

          <Modal.Body className="pt-6 md:pt-8">
            {(() => {
              const currency = getCurrencySymbol(statements[0]?.currencyID);
              const adj = Number(inputs.adjustment ?? 0) || 0;
              const adjustedTotal = selectedTotal + adj;

              return (
                <div className="space-y-6">
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                    <div className="text-sm text-gray-600">Statement Total</div>
                    <div className="mt-1 text-3xl font-extrabold text-gray-900">
                      {currency}
                      {selectedTotal.toFixed(2)} {selectedIds}
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="adjustment"
                      className="block mb-2 text-sm font-medium text-gray-900"
                    >
                      Adjustment
                    </label>
                    <div className="relative">
                      <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-gray-500">
                        {currency}
                      </span>
                      <input
                        type="number"
                        id="adjustment"
                        name="adjustment"
                        inputMode="decimal"
                        step="0.01"
                        value={inputs.adjustment ?? ""}
                        onChange={(e) =>
                          setInputs((p) => ({
                            ...p,
                            adjustment: e.target.value,
                          }))
                        }
                        className="w-full rounded-lg border border-gray-300 bg-white p-2.5 pl-9 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                        placeholder="0.00"
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between border-t pt-4">
                    <div className="text-sm text-gray-600">
                      Total after adjustment
                    </div>
                    <div className="text-xl font-semibold text-gray-900">
                      {currency}
                      {adjustedTotal.toFixed(2)}
                    </div>
                  </div>

                  <div className="flex justify-end gap-3 pt-2">
                    <button
                      type="button"
                      onClick={onCloseModal}
                      className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      Cancel
                    </button>

                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          await doAddMutation({
                            path: "advisor/statement/add",
                            body: {
                              advisor: searchAdvisor,
                              total: adjustedTotal,
                              adjustment: inputs.adjustment,
                              ids: Array.from(selectedIds)
                            },
                          });
                          setOpenModal(false);
                          setInputs({});
                        } catch (e) {
                          console.log(e);
                        }
                      }}
                      className="inline-flex items-center rounded-md bg-green-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600"
                    >
                      Add Statement
                    </button>
                  </div>
                </div>
              );
            })()}
          </Modal.Body>
        </Modal>
      </>
    );
  }
}
